import React, { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import slugg from 'slugg';
import layoutVars from '../../theme/layout-vars.json';
import PageSection from '../PageNavigation/PageSection';
import PageNavigationTop from '../PageNavigation/PageNavigationTop';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import styles from './VariableDisplayEngine.css';
import navStyles from './VariableDisplayEngineNav.css';
import { isTabletWidth } from '../../theme/layoutUtilities';
import {
    registerResizeHandler,
    unregisterResizeHandler
} from '../../utils/resize-handler';
import { setCardID } from '../../utils/Format/Format';
import * as Cards from '../../cards';
import { addKeysToVDEData } from './util';

const startCase = (str) => str.replace(/([A-Z])/g, ' $1').replace(/^\s/, '');

/**
 * no column or rows everything
 * is in the same element which
 * allows for mobile ordering
 */
const singleColumnLayoutGenerator = (layout, rawData, fundData) =>
    layout.rows && (
        <div className="cards-wrapper">
            {layout.rows.map(
                (row) =>
                    row.columns &&
                    row.columns.map(
                        (column) =>
                            column.cards &&
                            column.cards.map((card) => {
                                const CardComponent = Cards[card.key];
                                const cardData = {
                                    ...card,
                                    key: card.id,
                                    ...rawData,
                                    ...fundData
                                };
                                const { ticker, blacklist } = cardData;
                                if (
                                    Array.isArray(blacklist) &&
                                    blacklist.includes(ticker)
                                ) {
                                    return false;
                                }
                                return (
                                    typeof CardComponent === 'function' && (
                                        <div
                                            className={cn('card-wrapper', {
                                                [`mobile-order-${
                                                    card.mobileOrder
                                                }`]:
                                                    typeof card.mobileOrder ===
                                                    'number'
                                            })}
                                        >
                                            <ErrorBoundary
                                                key={`eb-${card.key}`}
                                                errorMsg={`error inside ${
                                                    card.key
                                                }`}
                                                render={() => (
                                                    <div>
                                                        Sorry,{' '}
                                                        {startCase(card.key)} is
                                                        unavailable right now.{' '}
                                                        <br />We&apos;re working
                                                        on it.
                                                    </div>
                                                )}
                                            >
                                                <div className={card.key}>
                                                    <CardComponent
                                                        {...cardData}
                                                    />
                                                </div>
                                            </ErrorBoundary>
                                        </div>
                                    )
                                );
                            })
                    )
            )}
            <style jsx>{styles}</style>
        </div>
    );

const layoutGenerator = (layout, rawData, fundData, sectionTitle) =>
    layout.rows && (
        <div className="rows-wrapper">
            {layout.rows.map((row) => (
                <div className={cn(`row ${slugg(sectionTitle)}`)} key={row.key}>
                    {row.columns &&
                        row.columns.map((column) => {
                            const width = `calc(${column.width * 100}% - ${
                                layoutVars['module-padding']
                            }px)`;
                            const columnStyle = column.width ? { width } : {};
                            return (
                                <div
                                    key={column.key}
                                    className={cn('column', {
                                        flex: !column.width
                                    })}
                                    style={columnStyle}
                                >
                                    {column.cards &&
                                        column.cards.map((card) => {
                                            const CardComponent =
                                                Cards[card.key];

                                            const cardData = {
                                                ...card,
                                                key: card.id,
                                                ...rawData,
                                                ...fundData
                                            };
                                            const {
                                                ticker,
                                                blacklist
                                            } = cardData;
                                            if (
                                                Array.isArray(blacklist) &&
                                                blacklist.includes(ticker)
                                            ) {
                                                return false;
                                            }
                                            return (
                                                typeof CardComponent ===
                                                    'function' && (
                                                    <div
                                                        className={cn(
                                                            'card-wrapper'
                                                        )}
                                                    >
                                                        <ErrorBoundary
                                                            id={setCardID(
                                                                card.key,
                                                                true
                                                            )}
                                                            key={`eb-${
                                                                card.key
                                                            }`}
                                                            errorMsg={`error inside ${
                                                                card.key
                                                            }`}
                                                            render={() => (
                                                                <div>
                                                                    Sorry,{' '}
                                                                    {startCase(
                                                                        card.key
                                                                    )}{' '}
                                                                    is
                                                                    unavailable
                                                                    right now.{' '}
                                                                    <br />We&apos;re
                                                                    working on
                                                                    it.
                                                                </div>
                                                            )}
                                                        >
                                                            <div
                                                                className={
                                                                    card.key
                                                                }
                                                            >
                                                                <CardComponent
                                                                    {...cardData}
                                                                />
                                                            </div>
                                                        </ErrorBoundary>
                                                    </div>
                                                )
                                            );
                                        })}
                                </div>
                            );
                        })}
                </div>
            ))}
            <style jsx>{styles}</style>
        </div>
    );

/**
 * @TODO
 * figure out why PageNavigationTop is being
 * reinstantiated/rebuilt and we are losing refs. So we no longer
 * Perhaps by adding a key to the children
 * this will help with this issue?
 */
const VariableDisplayEngine = ({ data, ...rawData }) => {
    const [singleColumn, setSingleColumn] = useState(isTabletWidth());
    const resize = useCallback(
        () => {
            const isSingleColumn = isTabletWidth();
            // we have a change
            if (singleColumn !== isSingleColumn) {
                setSingleColumn(singleColumn);
            }
        },
        [singleColumn, isTabletWidth]
    );

    useEffect(() => {
        registerResizeHandler(resize);
        return () => {
            unregisterResizeHandler(resize);
        };
    }, []);

    const { sections, ...fundData } = addKeysToVDEData(data);
    return (
        <div className="variable-display-engine">
            <PageNavigationTop targetId="top-nav">
                {sections.map(({ layout = {}, key, ...sectionProps }) => (
                    <PageSection key={key} {...sectionProps} {...rawData}>
                        {(singleColumn
                            ? singleColumnLayoutGenerator
                            : layoutGenerator)(
                            layout,
                            rawData,
                            fundData,
                            sectionProps.title
                        )}
                    </PageSection>
                ))}
            </PageNavigationTop>
            <style jsx>{navStyles}</style>
        </div>
    );
};

export default VariableDisplayEngine;
